<template>
  <div>
    <!-- eslint-disable-next-line vue/no-v-for-template-key -- Baseline eslint ignore please fix if working on this-->
    <template v-for="compliance in compliances" :key="compliance.name">
      <CompanyAddressCompanyAlert v-if="compliance.name === 'COMPANY_ADDRESS'" />
      <template v-if="compliance.name === 'IR35'">
        <Ir35FreelancerAlert
          v-if="!compliance.data.hasAnsweredAllQuestions"
          :freelancer="activeAccount"
          :has-answered-old-questions="compliance.data.hasAnsweredOldQuestions"
          :yjob="compliance.data.yjob"
        />
        <Ir35InsuranceFreelancerAlert
          v-if="!compliance.data.hasInsurance"
          :has-answered-all-questions="compliance.data.hasAnsweredAllQuestions"
          :yjob="compliance.data.yjob"
        />
      </template>
      <NlInsuranceFreelancerAlert
        v-if="
          compliance.name === 'NL_INSURANCE' &&
          !compliance.data.hasInsurance &&
          AccountType.isFreelancer(activeAccount.__typename)
        "
      />
      <NlInsuranceRecruiterAlert
        v-if="
          compliance.name === 'NL_INSURANCE' &&
          !compliance.data.hasInsurance &&
          AccountType.isRecruiter(activeAccount.__typename)
        "
      />
      <UsWorkerClassificationFreelancerAlert
        v-if="compliance.name === 'US_WORKER_CLASSIFICATION'"
        :freelancer="activeAccount"
      />
      <AddressIncompleteAlert
        v-if="compliance.name === 'ADDRESS'"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :hire="hire"
        :is-complete="compliance.isComplete"
      />
      <BankAccountAlert v-if="compliance.name === 'BANK_ACCOUNT'" :key="compliance.name" />
      <UsTaxAlert
        v-if="compliance.name === 'US_TAX'"
        :key="compliance.name"
        :compliance-data="compliance.data"
      />
      <PayeAcceptAlert
        v-if="compliance.name === 'PAYE_ACCEPT'"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :is-complete="compliance.isComplete"
      />
      <PayeSettingsRequiredAlert
        v-if="compliance.name === 'UK_PAYROLL'"
        :key="compliance.name"
        :compliance-data="compliance.data"
        :is-complete="compliance.isComplete"
      />
      <GlobalWorkerClassificationAlert
        v-if="compliance.name === 'GLOBAL_WORKER_CLASSIFICATION'"
        :key="compliance.name"
        :freelancer-id="activeAccount.localId"
      />
      <EmployerOfRecordAlert
        v-if="compliance.name === ComplianceName.EMPLOYER_OF_RECORD"
        :key="compliance.name"
        :compliance-data="compliance.data"
      />
      <RecruiterBusinessEntityAlert
        v-if="
          recruiterBusinessEntityCompliances().includes(compliance.name) &&
          AccountType.isRecruiter(activeAccount.__typename)
        "
        :key="compliance.name"
      />
      <BusinessEntityTypeUpdateAlert
        v-if="
          compliance.name === ComplianceName.BUSINESS_ENTITY_TYPE_UPDATE &&
          AccountType.isFreelancer(activeAccount.__typename)
        "
        :key="compliance.name"
        :compliance-data="compliance.data"
      />
      <IdentificationAlert
        v-if="
          compliance.name === ComplianceName.IDENTIFICATION &&
          AccountType.isFreelancer(activeAccount.__typename)
        "
        :key="compliance.name"
      />
      <FullNameIncompleteAlert
        v-if="compliance.name === ComplianceName.FULL_NAME"
        :key="compliance.name"
      />
      <InvalidVatAlert v-if="compliance.name === ComplianceName.VALID_VAT" :key="compliance.name" />
      <FreelancerBusinessEntityAlert
        v-if="
          freelancerBusinessEntityCompliances().includes(compliance.name) &&
          AccountType.isFreelancer(activeAccount.__typename)
        "
        :key="compliance.name"
        :market="compliance.data.market"
      />
    </template>
    <AccountComplianceAlerts
      :compliance-names="accountComplianceNames"
      @has-compliances="hasApiCompliances = $event"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'
import { ComplianceName as ApiComplianceName } from '@/gql/graphql'
import CompanyAddressCompanyAlert from '@/components/compliances/alerts/CompanyAddressCompanyAlert.vue'
import Ir35FreelancerAlert, {
  fragments as Ir35FreelancerAlertAccountFragments,
} from '@/components/compliances/alerts/Ir35FreelancerAlert.vue'
import BankAccountAlert from '@/components/compliances/alerts/BankAccountAlert.vue'
import UsTaxAlert from '@/components/compliances/alerts/UsTaxAlert.vue'
import PayeAcceptAlert from '@/components/compliances/alerts/PayeAcceptAlert.vue'
import PayeSettingsRequiredAlert from '@/components/compliances/alerts/PayeSettingsRequiredAlert.vue'
import AddressIncompleteAlert from '@/components/compliances/alerts/AddressIncompleteAlert.vue'
import Ir35InsuranceFreelancerAlert from '@/components/compliances/alerts/Ir35InsuranceFreelancerAlert.vue'
import NlInsuranceFreelancerAlert from '@/components/compliances/alerts/NlInsuranceFreelancerAlert.vue'
import NlInsuranceRecruiterAlert from '@/components/compliances/alerts/NlInsuranceRecruiterAlert.vue'
import UsWorkerClassificationFreelancerAlert, {
  fragments as UsWorkerClassificationFreelancerAlertFragments,
} from '@/components/compliances/alerts/UsWorkerClassificationFreelancerAlert.vue'
import { ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT } from '@/components/compliances/queries/Compliances'
import AccountType from '@/utils/AccountType'
import { ParseJSONPropertyInObject } from '@/utils/ParseJSONPropertyInObject'
import GlobalWorkerClassificationAlert from '@/components/compliances/alerts/GlobalWorkerClassificationAlert.vue'
import {
  ComplianceName,
  freelancerBusinessEntityCompliances,
  recruiterBusinessEntityCompliances,
} from '@/components/compliances/types/ComplianceName.enum'
import EmployerOfRecordAlert from '@/components/compliances/alerts/EmployerOfRecordAlert.vue'
import BusinessEntityTypeUpdateAlert from '@/components/compliances/alerts/BusinessEntityTypeUpdateAlert.vue'
import IdentificationAlert from '@/components/compliances/alerts/IdentificationAlert.vue'
import RecruiterBusinessEntityAlert from '@/components/compliances/BusinessEntity/alerts/RecruiterBusinessEntityAlert.vue'
import FreelancerBusinessEntityAlert from '@/components/compliances/BusinessEntity/alerts/FreelancerBusinessEntityAlert.vue'
import FullNameIncompleteAlert from '@/components/compliances/alerts/FullNameIncompleteAlert.vue'
import InvalidVatAlert from '@/components/compliances/alerts/InvalidVatAlert.vue'
import AccountComplianceAlerts from '@/components/compliances/alerts/AccountComplianceAlerts.vue'

const emit = defineEmits(['has-compliances'])

const complianceNames = [
  'COMPANY_ADDRESS',
  'IR35',
  'NL_INSURANCE',
  'US_WORKER_CLASSIFICATION',
  'ADDRESS',
  'BANK_ACCOUNT',
  'US_TAX',
  'PAYE_ACCEPT',
  'UK_PAYROLL',
  'GLOBAL_WORKER_CLASSIFICATION',
  'EMPLOYER_OF_RECORD',
  'BUSINESS_ENTITY_TYPE_UPDATE',
  'IDENTIFICATION',
  'FULL_NAME',
  'VALID_VAT',
  ...recruiterBusinessEntityCompliances(),
  ...freelancerBusinessEntityCompliances(),
]

// Rendered via AccountComplianceAlerts using API-provided title/description/action.
const accountComplianceNames = [
  ApiComplianceName.NlWaadiRegistration,
  ApiComplianceName.NlSnaCertification,
  ApiComplianceName.NlKvkRegistration,
  ApiComplianceName.NlBtwIdentification,
  ApiComplianceName.NlGAccount,
  ApiComplianceName.RecruiterNlKvkCertificate,
  ApiComplianceName.FreelancerNlKvkCertificate,
  ApiComplianceName.ClientOnboarding,
  ApiComplianceName.DeterminationHireFreelancerInput,
]

const { result } = useQuery(
  gql`
    query accountQuery($complianceNames: [ComplianceName!]!) {
      me {
        activeAccount {
          __typename
          ...ActiveCompliancesOnAccount
          ...Ir35FreelancerAlertAccount
          ...UsWorkerClassificationFreelancerAlertAccount
        }
      }
    }
    ${ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT}
    ${Ir35FreelancerAlertAccountFragments.account}
    ${UsWorkerClassificationFreelancerAlertFragments.account}
  `,
  {
    complianceNames,
  },
)

const activeAccount = computed(() => result?.value?.me?.activeAccount)
const compliances = computed(
  () =>
    activeAccount.value?.compliances
      ?.filter(({ name, isApplicable, isComplete }) => {
        if (!isApplicable) {
          return false
        }
        if (isComplete) {
          return false
        }
        if (
          name === 'IR35' ||
          name === 'US_WORKER_CLASSIFICATION' ||
          name === 'GLOBAL_WORKER_CLASSIFICATION' ||
          name === 'BUSINESS_ENTITY_TYPE_UPDATE' ||
          name === 'IDENTIFICATION'
        ) {
          return AccountType.isFreelancer(activeAccount.value.__typename)
        }
        if (name === 'NL_INSURANCE') {
          return (
            AccountType.isFreelancer(activeAccount.value.__typename) ||
            AccountType.isRecruiter(activeAccount.value.__typename)
          )
        }
        return true
      })
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)

const hasApiCompliances = ref(false)
const hasCompliances = computed(() => compliances.value.length > 0 || hasApiCompliances.value)

watch(hasCompliances, (newHasCompliances) => {
  emit('has-compliances', newHasCompliances)
})
</script>
